import React from 'react';
import {
  CheckCircle2, AlertTriangle, XCircle, TrendingUp, TrendingDown,
  Truck, Fuel, DollarSign, MapPin, Info, Copy,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  green:  { cls: 'bg-emerald-50 border-emerald-300 text-emerald-900',  pill: 'bg-emerald-600 text-white',   icon: CheckCircle2, dot: 'bg-emerald-500' },
  yellow: { cls: 'bg-amber-50 border-amber-300 text-amber-900',        pill: 'bg-amber-500 text-white',     icon: AlertTriangle, dot: 'bg-amber-500' },
  red:    { cls: 'bg-red-50 border-red-300 text-red-900',              pill: 'bg-red-600 text-white',       icon: XCircle, dot: 'bg-red-500' },
};

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtMoney2 = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

const Metric = ({ label, value, sub, accent, testid }) => (
  <div className="bg-white rounded-md border border-slate-200 p-3" data-testid={testid}>
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`text-2xl font-bold mt-0.5 ${accent || 'text-slate-900'}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</p>
    {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

const ProfitabilityPanel = ({ metrics, negotiationMessage, dense = false, bookingContext = null }) => {
  if (!metrics) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-300 rounded-md p-6 text-center text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        Enter a rate and total miles to see live profitability.
      </div>
    );
  }
  const meta = STATUS_META[metrics.status] || STATUS_META.yellow;
  const Icon = meta.icon;
  const profitAccent = metrics.profit >= 0 ? 'text-emerald-700' : 'text-red-700';
  const dh = metrics.deadhead_severity || 'green';
  const dhCls = dh === 'green' ? 'text-emerald-700' : dh === 'yellow' ? 'text-amber-700' : 'text-red-700';

  const copyNegotiation = () => {
    if (!negotiationMessage) return;
    navigator.clipboard.writeText(negotiationMessage);
    toast.success('Negotiation message copied');
  };

  return (
    <div className="space-y-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="profitability-panel">
      {/* Status hero */}
      <div className={`border rounded-md p-4 ${meta.cls}`} data-testid={`profitability-status-${metrics.status}`}>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <Icon className="w-7 h-7 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${meta.pill}`}>{metrics.status_label}</span>
                <span className="text-[10px] uppercase tracking-widest text-slate-600">{metrics.load_type_label}</span>
              </div>
              <p className="font-bold mt-1 text-lg" style={{ fontFamily: 'Work Sans, sans-serif' }}>{metrics.recommendation}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-70">Estimated Profit</p>
            <p className={`text-3xl font-bold ${profitAccent}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>{fmtMoney(metrics.profit)}</p>
            <p className="text-[10px] opacity-70">{fmtPct(metrics.margin_pct)} margin</p>
          </div>
        </div>
        {(metrics.status_reasons || []).length > 0 && (
          <ul className="mt-3 space-y-0.5">
            {metrics.status_reasons.map((r, i) => (
              <li key={i} className="text-[11px] flex items-start gap-1.5">
                <span className={`inline-block w-1 h-1 rounded-full mt-1.5 ${meta.dot}`} /> {r}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Top metrics row */}
      <div className={`grid ${dense ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-4'} gap-2`}>
        <Metric label="Revenue" value={fmtMoney(metrics.revenue)} sub={`${(metrics.total_miles || 0).toLocaleString()} mi total`} testid="metric-revenue" />
        <Metric label="RPM" value={fmtMoney2(metrics.rpm)} sub={`target ${fmtMoney2(metrics.rpm_target)}`} accent={metrics.rpm >= metrics.rpm_target ? 'text-emerald-700' : 'text-amber-700'} testid="metric-rpm" />
        <Metric label="CPM" value={fmtMoney2(metrics.cpm)} sub={`${fmtMoney(metrics.total_cost)} total cost`} testid="metric-cpm" />
        <Metric label="Break-Even" value={fmtMoney(metrics.break_even_revenue)} sub={`@ ${fmtMoney2(metrics.break_even_rpm)} RPM`} testid="metric-breakeven" />
      </div>

      {/* Deadhead + Fuel row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3" data-testid="metric-deadhead">
          <MapPin className={`w-5 h-5 ${dhCls}`} />
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Deadhead</p>
            <p className={`text-base font-bold ${dhCls}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>
              {(metrics.deadhead_miles || 0).toLocaleString()} mi
              <span className="text-xs font-normal ml-2 text-slate-500">({fmtMoney(metrics.deadhead_cost)} fuel)</span>
            </p>
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${dh === 'green' ? 'bg-emerald-100 text-emerald-700' : dh === 'yellow' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
            {dh === 'green' ? 'OK' : dh === 'yellow' ? 'Watch' : 'High'}
          </span>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3" data-testid="metric-fuel">
          <Fuel className="w-5 h-5 text-slate-600" />
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Fuel</p>
            <p className="text-base font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              {fmtMoney(metrics.fuel_cost)}
              <span className="text-xs font-normal ml-2 text-slate-500">{metrics.fuel_gallons} gal · @ ${metrics.fuel_price_per_gallon}/gal · MPG {metrics.fleet_mpg}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Cost breakdown */}
      <details className="bg-white border border-slate-200 rounded-md p-3" data-testid="cost-breakdown">
        <summary className="cursor-pointer text-xs font-semibold text-slate-700 select-none flex items-center justify-between">
          <span>Cost Breakdown</span>
          <span className="text-slate-400 text-[10px]">click to expand</span>
        </summary>
        <ul className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {Object.entries(metrics.cost_breakdown || {}).map(([k, v]) => (
            <li key={k} className="flex items-center justify-between bg-slate-50 rounded px-2 py-1.5">
              <span className="capitalize text-slate-600">{k.replace(/_/g, ' ')}</span>
              <span className="font-mono font-semibold text-slate-900">{fmtMoney(v)}</span>
            </li>
          ))}
        </ul>
        {(metrics.load_type_excludes || []).length > 0 && (
          <p className="text-[10px] text-slate-500 mt-2 flex items-center gap-1">
            <Info className="w-3 h-3" /> Excluded by load type: {metrics.load_type_excludes.join(', ')}
          </p>
        )}
      </details>

      {/* Booking-score panel (Phase 2 context) */}
      {bookingContext?.booking_score && (
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="booking-score-panel">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Booking Score</p>
              <p className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {bookingContext.booking_score.score}
                <span className="text-base text-slate-400 font-normal">/100</span>
              </p>
              <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${
                bookingContext.booking_score.rating === 'excellent' || bookingContext.booking_score.rating === 'good' ? 'bg-emerald-600 text-white' :
                bookingContext.booking_score.rating === 'review' ? 'bg-amber-500 text-white' : 'bg-red-600 text-white'
              }`} data-testid={`booking-rating-${bookingContext.booking_score.rating}`}>
                {bookingContext.booking_score.rating} · {bookingContext.booking_score.recommendation}
              </span>
            </div>
            <div className="flex flex-col items-end gap-1 text-xs">
              {bookingContext.customer && bookingContext.customer.load_count > 0 && (
                <div className="flex items-center gap-1.5" data-testid="customer-grade">
                  <span className="text-slate-500">Customer:</span>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    ['A+', 'A'].includes(bookingContext.customer.grade) ? 'bg-emerald-600 text-white' :
                    bookingContext.customer.grade === 'B' ? 'bg-amber-500 text-white' :
                    bookingContext.customer.grade === 'C' ? 'bg-orange-500 text-white' :
                    'bg-red-600 text-white'
                  }`}>{bookingContext.customer.grade}</span>
                  <span className="text-slate-500">({bookingContext.customer.load_count})</span>
                </div>
              )}
              {bookingContext.lane && bookingContext.lane.load_count > 0 && (
                <div className="flex items-center gap-1.5" data-testid="lane-strength">
                  <span className="text-slate-500">Lane:</span>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                    bookingContext.lane.strength === 'strong' ? 'bg-emerald-100 text-emerald-700' :
                    bookingContext.lane.strength === 'average' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>{bookingContext.lane.strength}</span>
                  <span className="text-slate-500">({bookingContext.lane.load_count})</span>
                </div>
              )}
              {typeof bookingContext.booking_score.fleet_delta_pct === 'number' && (
                <div className="text-[11px] tabular-nums">
                  vs Fleet avg:{' '}
                  <span className={bookingContext.booking_score.fleet_delta_pct >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>
                    {bookingContext.booking_score.fleet_delta_pct >= 0 ? '+' : ''}{bookingContext.booking_score.fleet_delta_pct}%
                  </span>
                </div>
              )}
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {bookingContext.booking_score.components.map((c) => (
              <li key={c.key} className="text-[11px] flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                <span className="text-slate-600 truncate pr-2">{c.label}</span>
                <span className="font-mono font-semibold text-slate-800 flex-shrink-0">{c.score}/{c.max}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Negotiation */}
      {negotiationMessage && metrics.status !== 'green' && (
        <div className="bg-slate-900 text-white rounded-md p-4" data-testid="negotiation-message">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Negotiation Assistant</p>
              <p className="text-sm leading-relaxed">{negotiationMessage}</p>
              {metrics.required_rate_delta > 0 && (
                <div className="mt-2 flex items-center gap-3 text-xs">
                  <span>Current: <span className="font-mono font-bold">{fmtMoney(metrics.revenue)}</span></span>
                  <TrendingUp className="w-3 h-3" />
                  <span>Required: <span className="font-mono font-bold text-emerald-300">{fmtMoney(metrics.required_revenue)}</span></span>
                  <span className="text-amber-300">(+{fmtMoney(metrics.required_rate_delta)})</span>
                </div>
              )}
            </div>
            <button
              onClick={copyNegotiation}
              data-testid="copy-negotiation"
              className="flex-shrink-0 inline-flex items-center gap-1 bg-white text-slate-900 hover:bg-slate-100 rounded text-xs font-semibold px-3 py-1.5"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Compact pill for load board rows
export const ProfitabilityPill = ({ metrics }) => {
  if (!metrics) {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-slate-100 text-slate-500">—</span>;
  }
  const meta = STATUS_META[metrics.status] || STATUS_META.yellow;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${meta.pill}`}
      title={`${metrics.status_label} · ${fmtMoney(metrics.profit)} (${fmtPct(metrics.margin_pct)})`}
      data-testid={`pp-pill-${metrics.status}`}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-white/70" />
      {metrics.status_label}
    </span>
  );
};

// Fast, context-free booking score (0-100) derived from a saved load's
// profitability sub-doc. Used by the load board to render a compact score
// chip next to the status pill without making per-row API calls.
// The FULL booking score (with customer/lane/fleet weighting) is fetched on
// demand inside the LoadEditModal via GET /api/profitability/booking-score/{id}.
export const quickBookingScore = (metrics) => {
  if (!metrics) return null;
  const status = metrics.status || 'yellow';
  const base = status === 'green' ? 60 : status === 'yellow' ? 35 : 0;
  const marginPct = Number(metrics.margin_pct) || 0;
  // up to +25 for margin (0% margin = +0, 25% margin = +25, capped)
  const marginBoost = Math.min(25, Math.max(0, marginPct));
  // up to +15 for deadhead (green=15, yellow=8, red=0)
  const dh = metrics.deadhead_severity || 'green';
  const dhBoost = dh === 'green' ? 15 : dh === 'yellow' ? 8 : 0;
  const score = Math.round(Math.max(0, Math.min(100, base + marginBoost + dhBoost)));
  let rating = 'review';
  if (score >= 90) rating = 'excellent';
  else if (score >= 70) rating = 'good';
  else if (score < 50) rating = 'avoid';
  return { score, rating };
};

export const BookingScoreChip = ({ metrics, full = null }) => {
  // `full` is the optional richer score from the API; otherwise fall back to quickBookingScore.
  const data = full || quickBookingScore(metrics);
  if (!data) return null;
  const cls = data.rating === 'excellent' || data.rating === 'good'
    ? 'bg-emerald-100 text-emerald-700'
    : data.rating === 'review'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-red-100 text-red-700';
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${cls}`}
      title={`Booking score ${data.score}/100 (${data.rating})`}
      data-testid={`booking-chip-${data.rating}`}
    >
      {data.score}
    </span>
  );
};

export default ProfitabilityPanel;
